import { sharedLinksApi } from './api/shared-links';
import type { SharedLinkClaimResponse } from './api/types';
import { apiErrorMessage } from './api/errors';

const PENDING_CLAIM_KEY = 'pending_shared_link_claim';

function getStorage(): Storage | null {
  return typeof sessionStorage === 'undefined' ? null : sessionStorage;
}

export function storePendingClaimToken(token: string): void {
  getStorage()?.setItem(PENDING_CLAIM_KEY, token);
}

export function getPendingClaimToken(): string | null {
  return getStorage()?.getItem(PENDING_CLAIM_KEY) ?? null;
}

export function clearPendingClaimToken(): void {
  getStorage()?.removeItem(PENDING_CLAIM_KEY);
}

/**
 * Claims a pending anonymous shared link into the authenticated tenant.
 * The token is cleared before the request fires, so strict-mode double
 * effects, retries, and page refreshes can never claim the same token twice.
 * Returns null when no claim is pending; rethrows on API failure so the
 * caller can surface a non-blocking toast.
 */
export async function claimPendingSharedLink(): Promise<SharedLinkClaimResponse | null> {
  const token = getPendingClaimToken();
  if (!token) {
    return null;
  }
  clearPendingClaimToken();
  return sharedLinksApi.claim({ claim_token: token });
}


export type ClaimOutcome =
  | { status: 'none' }
  | { status: 'claimed'; mappingId: string }
  | { status: 'failed' };

/**
 * Redeem a pending claim after any successful sign-in.
 *
 * This deliberately never throws. Claiming is a bonus on top of authentication,
 * and the token is spent by claimPendingSharedLink before the request fires, so
 * a failure here is final either way — surfacing it as an outcome keeps every
 * caller from re-implementing the same try/catch around a non-blocking step.
 */
export async function redeemPendingClaim(): Promise<ClaimOutcome> {
  try {
    const claimed = await claimPendingSharedLink();
    if (!claimed) return { status: 'none' };
    return { status: 'claimed', mappingId: claimed.mapping_id };
  } catch {
    return { status: 'failed' };
  }
}

/**
 * True when registration failed because the address already has an account.
 *
 * This is the exact dead end that stranded anonymous links: the website's only
 * route out of the promotion screen was "create an account", so anyone who
 * already had one could never claim the link they had just made. Matching the
 * backend's message (routers/auth.py: "Email already registered") lets the
 * register page offer signing in instead, which redeems the stored token.
 */
export function isEmailTakenError(error: unknown): boolean {
  return /already registered/i.test(apiErrorMessage(error, ''));
}
